import type { ReactElement } from "react"
import { Link } from "react-router"
import styled from "styled-components"
import { BackArrowIcon } from "./icons"

/**
 * The player's back control, on a light page.
 *
 * Same geometry and same `arrow-left` glyph as `PlayerControls` draws, so leaving
 * a screen looks the same wherever the customer is. What changes is the fill: the
 * player's is transparent over a scrim that guarantees contrast, and a detail page
 * has no scrim — over artwork or over `pageBackground`, the circle has to carry
 * its own surface.
 */
const Control = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  align-self: flex-start;
  width: 2.125rem;
  height: 2.125rem;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  background-color: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;
  transition: background-color 120ms ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.color.surfaceSubtle};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`

interface BackButtonProps {
  readonly to: string
  /** The accessible name. The glyph says "back", not "back to what". */
  readonly label: string
  readonly qaHook: string
}

export const BackButton = ({ to, label, qaHook }: BackButtonProps): ReactElement => (
  <Control to={to} aria-label={label} title={label} data-qa-hook={qaHook}>
    <BackArrowIcon size={20} />
  </Control>
)
